import type { AxisSelection } from '@atlas/types'
import { FilterRail, FilterRailSkeleton } from '@atlas/ui-components'
import { Chip } from '@atlas/ui-primitives'
import { X } from 'lucide-react'
import { useFilterRailAxes } from '@/hooks/useLibrary'

export interface BoardFiltersProps {
	shownAxisCodes: string[]
	selection: AxisSelection
	onToggle: (axisCode: string, valueCode: string) => void
	onAddAxis: (axisCode: string) => void
}

/**
 * The board's axis filter rail — reuses the promoted `@atlas/ui-components`
 * FilterRail, fed by the registry axes (same hook the Library uses). Presentational
 * wrapper: state lives in the Board.
 */
export function BoardFilters({ shownAxisCodes, selection, onToggle, onAddAxis }: BoardFiltersProps) {
	const { axes, isLoading } = useFilterRailAxes(shownAxisCodes)
	if (isLoading) {
		return (
			<div className="w-64 shrink-0">
				<FilterRailSkeleton title="Filter board" groupCount={shownAxisCodes.length} label="Loading filters…" />
			</div>
		)
	}
	return (
		<div className="w-64 shrink-0 overflow-y-auto">
			<FilterRail
				axes={axes}
				shownAxisCodes={shownAxisCodes}
				selection={selection}
				onToggle={onToggle}
				onAddAxis={onAddAxis}
				title="Filter board"
			/>
		</div>
	)
}

/** Removable chips summarising the active axis-value selection, above the board. */
export function ActiveFilterChips({
	selection,
	onRemove,
	onClear,
}: {
	selection: AxisSelection
	onRemove: (axisCode: string, valueCode: string) => void
	onClear: () => void
}) {
	const active = Object.entries(selection).flatMap(([axisCode, codes]) =>
		(codes ?? []).map((valueCode) => ({ axisCode, valueCode })),
	)
	if (active.length === 0) return null
	return (
		<div className="mb-4 flex flex-wrap items-center gap-1.5">
			{active.map(({ axisCode, valueCode }) => (
				<button key={`${axisCode}:${valueCode}`} type="button" onClick={() => onRemove(axisCode, valueCode)}>
					<Chip variant="secondary" size="sm" className="gap-1">
						{axisCode}: {valueCode}
						<X className="h-3 w-3" aria-hidden="true" />
					</Chip>
				</button>
			))}
			<button type="button" onClick={onClear} className="text-xs text-ds-text-subtle underline hover:text-ds-text">
				Clear all
			</button>
		</div>
	)
}

/** True if a work order's tuple satisfies EVERY selected axis (AND across axes, OR within). */
export function matchesSelection(tuple: AxisSelection | undefined, selection: AxisSelection): boolean {
	return Object.entries(selection).every(([axisCode, wanted]) => {
		if (!wanted || wanted.length === 0) return true
		const have = tuple?.[axisCode] ?? []
		return wanted.some((w) => have.includes(w))
	})
}
